import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Plus, Search, User, Building, Phone, Mail, 
  ArrowUpCircle, ArrowDownCircle, Clock, DollarSign, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const CreditAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewCustomerDialog, setShowNewCustomerDialog] = useState(false);
  const [showNewAccountDialog, setShowNewAccountDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '', phone: '', email: '', company: '', address: ''
  });
  const [newAccount, setNewAccount] = useState({
    customer_id: '', credit_limit: 10000, payment_frequency: 'monthly', payment_due_day: 1
  });
  const [payment, setPayment] = useState({
    account_id: '', amount: '', payment_method: 'cash', notes: ''
  });

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/credit/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        }
      });
      const data = await response.json();
      setAccounts(data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/credit/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        }
      });
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchAccountDetails = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/credit/accounts/${accountId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        }
      });
      const data = await response.json();
      setSelectedAccount(data);
    } catch (error) {
      console.error('Failed to fetch account details:', error);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchCustomers();
  }, []);

  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone) {
      alert('Name and phone are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/credit/customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        },
        body: JSON.stringify(newCustomer)
      });

      if (response.ok) {
        setShowNewCustomerDialog(false);
        setNewCustomer({ name: '', phone: '', email: '', company: '', address: '' });
        fetchCustomers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create customer');
      }
    } catch (error) {
      console.error('Failed to create customer:', error);
    }
  };

  const handleCreateAccount = async () => {
    if (!newAccount.customer_id) {
      alert('Please select a customer');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/credit/accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        },
        body: JSON.stringify(newAccount)
      });

      if (response.ok) {
        setShowNewAccountDialog(false);
        setNewAccount({ customer_id: '', credit_limit: 10000, payment_frequency: 'monthly', payment_due_day: 1 });
        fetchAccounts();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create account');
      }
    } catch (error) {
      console.error('Failed to create account:', error);
    }
  };

  const handleRecordPayment = async () => {
    if (!payment.amount || parseFloat(payment.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/credit/payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        },
        body: JSON.stringify({
          ...payment,
          amount: parseFloat(payment.amount)
        })
      });

      if (response.ok) {
        setShowPaymentDialog(false);
        setPayment({ account_id: '', amount: '', payment_method: 'cash', notes: '' });
        fetchAccounts();
        if (selectedAccount) {
          fetchAccountDetails(selectedAccount.id);
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
    }
  };

  const filteredAccounts = accounts.filter(account =>
    account.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.customer_phone?.includes(searchTerm) ||
    account.customer_company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const customersWithoutAccount = customers.filter(
    c => !c.credit_account
  );

  return (
    <div className="p-6" data-testid="credit-accounts-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Credit Accounts</h1>
          <p className="text-gray-500">Manage customer credit accounts and payments</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowNewCustomerDialog(true)} data-testid="add-customer-btn">
            <User className="w-4 h-4 mr-2" />
            Add Customer
          </Button>
          <Button onClick={() => setShowNewAccountDialog(true)} data-testid="create-account-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Account
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Accounts List */}
        <div className="lg:col-span-1 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : filteredAccounts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No credit accounts found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredAccounts.map((account) => (
                  <button
                    key={account.id}
                    onClick={() => fetchAccountDetails(account.id)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedAccount?.id === account.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                    data-testid={`account-${account.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          account.current_balance > 0 ? 'bg-amber-100' : 'bg-green-100'
                        }`}>
                          {account.customer_company ? (
                            <Building className={`w-5 h-5 ${account.current_balance > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                          ) : (
                            <User className={`w-5 h-5 ${account.current_balance > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{account.customer_name}</p>
                          <p className="text-sm text-gray-500">{account.customer_phone}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${account.current_balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          ₹{account.current_balance?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-gray-400">
                          Limit: ₹{account.credit_limit?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Account Details */}
        <div className="lg:col-span-2">
          {selectedAccount ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm">
                      <CreditCard className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{selectedAccount.customer_name}</h2>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        {selectedAccount.customer?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {selectedAccount.customer.phone}
                          </span>
                        )}
                        {selectedAccount.customer?.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {selectedAccount.customer.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      setPayment({ ...payment, account_id: selectedAccount.id });
                      setShowPaymentDialog(true);
                    }}
                    data-testid="record-payment-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Record Payment
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Current Balance</p>
                    <p className={`text-2xl font-bold ${selectedAccount.current_balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      ₹{selectedAccount.current_balance?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Credit Limit</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ₹{selectedAccount.credit_limit?.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Available Credit</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ₹{(selectedAccount.credit_limit - selectedAccount.current_balance)?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Transactions */}
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Transaction History
                </h3>
                
                {selectedAccount.transactions?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No transactions yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedAccount.transactions?.map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            tx.transaction_type === 'payment' ? 'bg-green-100' : 'bg-amber-100'
                          }`}>
                            {tx.transaction_type === 'payment' ? (
                              <ArrowDownCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <ArrowUpCircle className="w-4 h-4 text-amber-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{tx.description}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(tx.created_at).toLocaleDateString()} at {new Date(tx.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${
                            tx.transaction_type === 'payment' ? 'text-green-600' : 'text-amber-600'
                          }`}>
                            {tx.transaction_type === 'payment' ? '-' : '+'}₹{tx.amount?.toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-400">
                            Balance: ₹{tx.balance_after?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Account</h3>
              <p className="text-gray-500">Choose a credit account from the list to view details and transactions</p>
            </div>
          )}
        </div>
      </div>

      {/* New Customer Dialog */}
      <Dialog open={showNewCustomerDialog} onOpenChange={setShowNewCustomerDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Name *</label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="Customer name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Phone *</label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                placeholder="Email address"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Company</label>
              <Input
                value={newCustomer.company}
                onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
                placeholder="Company name"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowNewCustomerDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomer}>Create Customer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Account Dialog */}
      <Dialog open={showNewAccountDialog} onOpenChange={setShowNewAccountDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Credit Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Customer *</label>
              <Select
                value={newAccount.customer_id}
                onValueChange={(value) => setNewAccount({ ...newAccount, customer_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customersWithoutAccount.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Credit Limit (₹)</label>
              <Input
                type="number"
                value={newAccount.credit_limit}
                onChange={(e) => setNewAccount({ ...newAccount, credit_limit: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Payment Frequency</label>
              <Select
                value={newAccount.payment_frequency}
                onValueChange={(value) => setNewAccount({ ...newAccount, payment_frequency: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="on_demand">On Demand</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowNewAccountDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAccount}>Create Account</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Amount (₹) *</label>
              <Input
                type="number"
                value={payment.amount}
                onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Payment Method</label>
              <Select
                value={payment.payment_method}
                onValueChange={(value) => setPayment({ ...payment, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <Input
                value={payment.notes}
                onChange={(e) => setPayment({ ...payment, notes: e.target.value })}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment}>Record Payment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditAccounts;
